
import { useEffect, useRef, useState } from "react";
import { useMarketingLang } from "../i18n.jsx";
// next/image replaced — using native <img>

export default function Bento() {
  const { t } = useMarketingLang();
  return (
    <section className="section-y border-t-[0.5px] border-white/[0.06]">
      <div className="container-page">
        <p className="eyebrow mb-6">{t('bento.eyebrow')}</p>
        <h2 className="h-section max-w-[680px] text-ink">
          {t('bento.title')}
        </h2>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 md:auto-rows-[230px]">
          {/* Large image tile */}
          <Tile className="md:col-span-2 md:row-span-2 p-0 overflow-hidden">
            <div className="relative w-full h-full min-h-[300px]">
              <img
                src="/product/02-site-analysis.png"
                alt="SEIDR grid intelligence map"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-[1.03] transition-transform duration-[900ms]"
              />
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(180deg, transparent 35%, rgba(15,20,32,0.85) 95%)",
                }}
              />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-teal mb-2">
                  {t('bento.gridIntelligenceLabel')}
                </p>
                <p className="text-[19px] md:text-[22px] text-ink leading-[1.3] max-w-[460px]">
                  {t('bento.gridIntelligenceDesc')}
                </p>
              </div>
            </div>
          </Tile>

          {/* Stat: 5.4 GW */}
          <Tile>
            <CountUp to={5400} suffix=" MW" color="amber" />
            <p className="mt-2 text-[13px] text-white/65 leading-[1.45]">
              {t('bento.statGwDesc')}
            </p>
          </Tile>

          {/* Score UI fragment */}
          <Tile>
            <p className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-white/55 mb-3">
              {t('bento.liveScoring')}
            </p>
            <div className="flex items-baseline gap-2">
              <CountUp to={76} color="amber" small />
              <span className="text-[13px] text-white/65">{t('bento.suitable')}</span>
            </div>
            <div className="mt-4 h-[3px] bg-white/[0.08] rounded-full overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{ width: "76%", background: "#f0b23a" }}
              />
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2">
              {t('bento.scoreCategories').map((k) => (
                <div key={k} className="flex flex-col gap-1">
                  <span className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-white/45">
                    {k}
                  </span>
                  <span className="h-[2px] rounded-full bg-teal/70" />
                </div>
              ))}
            </div>
          </Tile>

          {/* Side-by-side comparison crop */}
          <Tile className="p-0 overflow-hidden">
            <div className="relative w-full h-full min-h-[200px]">
              <img
                src="/product/03-compare.jpeg"
                alt="SEIDR side-by-side site comparison"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-[1.03] transition-transform duration-[900ms]"
              />
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(180deg, transparent 30%, rgba(15,20,32,0.9) 100%)",
                }}
              />
              <div className="absolute bottom-5 left-5 right-5">
                <p className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-teal mb-1.5">
                  {t('bento.sideBySideLabel')}
                </p>
                <p className="text-[14px] text-ink/90 leading-[1.4]">
                  {t('bento.sideBySideDesc')}
                </p>
              </div>
            </div>
          </Tile>

          {/* Stat: 100× */}
          <Tile>
            <CountUp to={100} suffix="×" color="teal" />
            <p className="mt-2 text-[13px] text-white/65 leading-[1.45]">
              {t('bento.scenariosDesc')}
            </p>
          </Tile>

          {/* Sources */}
          <Tile>
            <p className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-white/55 mb-3">
              {t('bento.dataSourcesLabel')}
            </p>
            <ul className="flex flex-col gap-2">
              {t('bento.dataSources').map((s) => (
                <li
                  key={s}
                  className="flex items-center gap-2.5 text-[13px] text-ink/85"
                >
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-teal" />
                  {s}
                </li>
              ))}
            </ul>
          </Tile>
        </div>
      </div>
    </section>
  );
}

function Tile({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`group relative rounded-2xl bg-bg-card border-hairline-base transition-colors hover:border-white/15 ${
        className.includes("p-0") ? "" : "p-6"
      } ${className}`}
    >
      {children}
    </div>
  );
}

function CountUp({
  to,
  suffix = "",
  color = "teal",
  small = false,
}: {
  to: number;
  suffix?: string;
  color?: "teal" | "amber";
  small?: boolean;
}) {
  const [value, setValue] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const started = useRef(false);

  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const duration = 1200;
          const start = performance.now();
          const tick = (now: number) => {
            const p = Math.min(1, (now - start) / duration);
            const eased = 1 - Math.pow(1 - p, 3);
            setValue(Math.round(to * eased));
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.4 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [to]);

  const colorHex = color === "amber" ? "#f0b23a" : "#4dd4e8";
  const fontSize = small ? "32px" : "44px";

  return (
    <div
      ref={ref}
      className="font-mono tabular-nums leading-none"
      style={{ color: colorHex, fontSize }}
    >
      {value.toLocaleString()}
      {suffix}
    </div>
  );
}
